/**
 * @desc: 筛选器操作按钮区（确认/重置/扩展操作）
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Button, Justify } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import { Bubble } from '@home/components/TeaComponents';

interface IFilterActionBarProps extends IBaseProps {
  /** loading 状态，loading 时操作按钮禁用 */
  loading?: boolean;
  /** 筛选器值是否未被修改 */
  pristine: boolean;
  /** 是否展示吸顶态关闭入口 */
  showStickyClose?: boolean;
  /** 筛选器区域是否溢出 */
  hasOverflow: boolean;
  /** 是否展开 */
  expanded: boolean;
  /** 重置按钮 tooltip */
  resetTooltip?: string;
  /** Filter 内部注入的额外操作内容（Save 按钮等） */
  innerExtraActions?: React.ReactNode;
  /** 暴露给外部页面的额外操作内容 */
  extraActions?: React.ReactNode;
  /** 确认回调 */
  onConfirm: () => void;
  /** 重置回调 */
  onReset: () => void;
  /** 展开/收起回调 */
  onToggleExpand: () => void;
  /** 吸顶展开态关闭回调 */
  onStickyClose?: () => void;
}

const FilterActionBar: React.FC<IFilterActionBarProps> = (props) => {
  const {
    style,
    className,
    loading = false,
    pristine,
    showStickyClose = false,
    hasOverflow,
    expanded,
    resetTooltip,
    innerExtraActions,
    extraActions,
    onConfirm,
    onReset,
    onToggleExpand,
    onStickyClose,
  } = props;

  const { formatMessage } = useIntl();

  return (
    <div style={style} className={classnames('dbi-searchbox-action-bar', className)}>
      <Justify
        left={
          <>
            <Button type="primary" disabled={loading || pristine} onClick={onConfirm}>
              {formatMessage({ id: 'dbi_confirm' })}
            </Button>
            <Bubble className="dbi-bubble-max-width" dark content={resetTooltip}>
              <Button type="weak" disabled={loading} onClick={onReset}>
                {formatMessage({ id: 'dbi_reset' })}
              </Button>
            </Bubble>
            {innerExtraActions}
            {extraActions}
          </>
        }
        right={(() => {
          // 筛选器吸顶时，展示右下角的 Close 按钮
          if (showStickyClose) {
            return (
              <button
                className="dbi-searchbox-btn dbi-searchbox-btn--sticky-close"
                type="button"
                onClick={onStickyClose}
              >
                <span>{formatMessage({ id: 'dbi_close' })}</span>
                <SvgIcon type="point-top" className="dbi-searchbox-btn__arrow" />
              </button>
            );
          }

          // 筛选器没有吸顶，并且没有出现溢出的情况下，不展示任何内容
          if (!hasOverflow) {
            return null;
          }

          // 筛选器没有吸顶，并且出现了溢出的情况下，右下角展示 More Filter / Collapse Filter 按钮
          return (
            <Button className="dbi-searchbox-btn" type="link" onClick={onToggleExpand}>
              {expanded ? formatMessage({ id: 'dbi_collapse_filter' }) : formatMessage({ id: 'dbi_more_filter' })}
            </Button>
          );
        })()}
      />
    </div>
  );
};

export default FilterActionBar;
